// keyrd_console/src/components/PilotOperationsQueue.js
import React, { useEffect, useMemo, useState } from "react";
import { getPilotOperationsQueue } from "../api/pilotAdmin";
import PilotGovernanceBanner from "./PilotGovernanceBanner";
import PilotStatusPill from "./PilotStatusPill";
import PilotDataSourceBadge from "./PilotDataSourceBadge";

const pageStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
};

const titleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle = {
  color: "#475569",
  fontSize: 14,
  marginTop: 4,
};

const controlRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  minWidth: 260,
};

const selectStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
};

const queueStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const itemStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  cursor: "pointer",
};

const itemTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
};

const itemTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: "#0f172a",
};

const metaTextStyle = {
  fontSize: 13,
  color: "#64748b",
};

const rowWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const loadingStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
};

const errorStyle = {
  padding: 14,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

const emptyStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
};

function normalizeRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.queue)) return payload.queue;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function matchesQuery(row, query) {
  if (!query) return true;
  const haystack = [
    row.participant_uuid,
    row.participant_label,
    row.queue_type,
    row.queue_reason,
    row.priority,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query.toLowerCase());
}

function filterRows(rows, query, queueType) {
  return rows.filter((row) => {
    const queryOk = matchesQuery(row, query);
    const typeOk =
      queueType === "all" ||
      String(row.queue_type || "").toLowerCase() === queueType;
    return queryOk && typeOk;
  });
}

function PilotOperationsQueue({ auth, onSelectParticipant }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [query, setQuery] = useState("");
  const [queueType, setQueueType] = useState("all");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await getPilotOperationsQueue(auth);
        if (!cancelled) {
          setPayload(res?.data || res || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load pilot operations queue.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const data = payload || {};
  const rows = normalizeRows(data);
  const filteredRows = useMemo(
    () => filterRows(rows, query, queueType),
    [rows, query, queueType]
  );

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>Loading operations queue…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={titleRowStyle}>
        <div>
          <h1 style={titleStyle}>Operations Queue</h1>
          <div style={subtitleStyle}>
            Action-oriented queue for reviews, missing readiness items, held participants, and unresolved issues.
          </div>
        </div>
        <PilotDataSourceBadge source={data.data_source} stub={Boolean(data.stub)} />
      </div>

      <PilotGovernanceBanner auth={auth} system={data.system || data} />

      <div style={controlRowStyle}>
        <input
          style={inputStyle}
          type="text"
          value={query}
          placeholder="Search participant, priority, reason…"
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          style={selectStyle}
          value={queueType}
          onChange={(e) => setQueueType(e.target.value)}
        >
          <option value="all">All queue types</option>
          <option value="review_pending">Review Pending</option>
          <option value="missing_readiness">Missing Readiness</option>
          <option value="held">Held</option>
          <option value="paused_followup">Paused Follow-up</option>
          <option value="alert_resolution">Alert Resolution</option>
          <option value="governance_mismatch">Governance Mismatch</option>
        </select>
      </div>

      {!filteredRows.length ? (
        <div style={emptyStyle}>No queue items matched the current filters.</div>
      ) : (
        <div style={queueStyle}>
          {filteredRows.map((row, idx) => (
            <div
              key={`${row.participant_uuid || "na"}-${row.queue_type || "na"}-${idx}`}
              style={itemStyle}
              onClick={() =>
                typeof onSelectParticipant === "function" &&
                row.participant_uuid &&
                onSelectParticipant(row.participant_uuid)
              }
            >
              <div style={itemTopStyle}>
                <div>
                  <div style={itemTitleStyle}>
                    {row.participant_label || "Unnamed Participant"}
                  </div>
                  <div style={metaTextStyle}>{row.participant_uuid || "No UUID"}</div>
                </div>
                <div style={rowWrapStyle}>
                  <PilotStatusPill
                    status={row.priority || "unknown"}
                    label={`Priority: ${row.priority || "unknown"}`}
                  />
                  <PilotStatusPill
                    status={row.queue_type || "unknown"}
                    label={row.queue_type || "unknown"}
                  />
                </div>
              </div>

              <div style={metaTextStyle}>
                Reason: {row.queue_reason || "No queue reason supplied."}
              </div>

              <div style={rowWrapStyle}>
                <PilotStatusPill
                  status={row.lifecycle_state || "unknown"}
                  label={`Lifecycle: ${row.lifecycle_state || "unknown"}`}
                />
                <PilotStatusPill
                  status={row.operator_review_status || "unknown"}
                  label={`Review: ${row.operator_review_status || "unknown"}`}
                />
              </div>

              <div style={metaTextStyle}>
                Last Updated: {row.last_updated_utc || "unknown"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PilotOperationsQueue;